import { useState } from 'react'
import { useNewsEvents } from '@/hooks/useNewsEvents'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { RefreshCw, Search, Download } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { ImpactLevel } from '@/types/news'

const getImpactColor = (impact: ImpactLevel) => {
  const colors = {
    high: 'bg-red-500 text-white',
    medium: 'bg-amber-500 text-white',
    low: 'bg-emerald-500 text-white',
    none: 'bg-gray-500 text-white',
  }
  return colors[impact] || colors.none
}

export function CalendarDashboard() {
  const { events, isLoading, fetchExternal, isFetching } = useNewsEvents()
  const [searchTerm, setSearchTerm] = useState('')
  const [impactFilter, setImpactFilter] = useState<ImpactLevel | 'all'>('all')

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.currency.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesImpact = impactFilter === 'all' || event.impact === impactFilter
    return matchesSearch && matchesImpact
  })

  const handleRefresh = async () => {
    try {
      await fetchExternal()
    } catch (error) {
      console.error('Error fetching external news:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">Loading news events...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">News Calendar</h1>
          <p className="text-muted-foreground">
            Manage forex news events and auto-close triggers
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            {isFetching ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Fetch Latest
          </Button>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Events</CardDescription>
            <CardTitle className="text-3xl">{events.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>High Impact</CardDescription>
            <CardTitle className="text-3xl text-red-500">
              {events.filter(e => e.impact === 'high').length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Auto-Close Enabled</CardDescription>
            <CardTitle className="text-3xl text-emerald-500">
              {events.filter(e => e.auto_close_enabled).length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Currencies</CardDescription>
            <CardTitle className="text-3xl">
              {new Set(events.map(e => e.currency)).size}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search events or currencies..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="ml-4 flex gap-2">
              <Button
                variant={impactFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setImpactFilter('all')}
              >
                All
              </Button>
              <Button
                variant={impactFilter === 'high' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setImpactFilter('high')}
              >
                High
              </Button>
              <Button
                variant={impactFilter === 'medium' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setImpactFilter('medium')}
              >
                Medium
              </Button>
              <Button
                variant={impactFilter === 'low' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setImpactFilter('low')}
              >
                Low
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-3 text-left text-sm font-medium">Date/Time</th>
                  <th className="p-3 text-left text-sm font-medium">Currency</th>
                  <th className="p-3 text-left text-sm font-medium">Event</th>
                  <th className="p-3 text-left text-sm font-medium">Impact</th>
                  <th className="p-3 text-left text-sm font-medium">Auto-Close</th>
                  <th className="p-3 text-left text-sm font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No events found
                    </td>
                  </tr>
                ) : (
                  filteredEvents.map((event) => (
                    <tr key={event.id} className="border-b hover:bg-muted/50">
                      <td className="p-3 text-sm">{formatDate(event.event_time)}</td>
                      <td className="p-3">
                        <Badge variant="outline">{event.currency}</Badge>
                      </td>
                      <td className="p-3 text-sm">{event.title}</td>
                      <td className="p-3">
                        <Badge className={getImpactColor(event.impact)}>
                          {event.impact.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {event.auto_close_enabled ? (
                          <Badge className="bg-emerald-500 text-white">
                            {event.auto_close_minutes_before}min
                          </Badge>
                        ) : (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {event.source || 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
